import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAState {
  isInstallable: boolean;
  isInstalled: boolean;
  isOnline: boolean;
  isUpdateAvailable: boolean;
  promptInstall: () => Promise<boolean>;
  checkForUpdates: () => Promise<void>;
}

export function usePWA(): PWAState {
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = (window.navigator as any).standalone === true;
    setIsInstalled(isStandalone || isIOSStandalone);

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
      
      toast({
        title: "📱 Instala Sentinel Crop",
        description: "Instala la app para acceso rápido y funcionalidad offline",
        duration: 8000,
      });
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for successful installation
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      
      toast({
        title: "✅ App instalada",
        description: "Sentinel Crop está lista para usar offline",
      });
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    // Monitor online/offline status
    const handleOnline = () => {
      setIsOnline(true);
      toast({
        title: "🌐 Conexión restaurada",
        description: "Sincronizando datos...",
      });
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast({
        title: "📡 Sin conexión",
        description: "Trabajando en modo offline",
        variant: "destructive",
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Service Worker is registered in main.tsx via serviceWorkerRegistration.ts
    // We only listen for updates here to show toast notifications
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        // Listen for updates (only show toast, don't register again)
        let updateToastShown = false;
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller && !updateToastShown) {
              updateToastShown = true;
              setIsUpdateAvailable(true);
              toast({
                title: "🔄 Actualización disponible",
                description: "Hay una nueva versión. Recarga para actualizar.",
                duration: 0,
              });
            }
          });
        });
      });
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [toast]);

  const promptInstall = async (): Promise<boolean> => {
    if (!deferredPrompt) {
      toast({
        title: "ℹ️ Ya instalada",
        description: "La app ya está instalada o no está disponible para instalar",
      });
      return false;
    }

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        toast({
          title: "✅ Instalación iniciada",
          description: "Sentinel Crop se está instalando...",
        });
        setDeferredPrompt(null);
        setIsInstallable(false);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Install prompt error:', error);
      return false;
    }
  };

  const checkForUpdates = async () => {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        await registration.update();
      }
    }
  };

  return {
    isInstallable,
    isInstalled,
    isOnline,
    isUpdateAvailable,
    promptInstall,
    checkForUpdates,
  };
}
